import clsx from 'clsx'
import first from 'lodash/first'
import includes from 'lodash/includes'
import map from 'lodash/map'
import React, { FC, useCallback, useContext, useEffect, useRef } from 'react'

import { useAmplitude } from '@Analytics/hook'
import { ProductContext } from '@Components/Product/Context'
import { ImageVertical } from '@Components/Product/Slider/Components/Image/ImageVertical'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { VideoVertical } from '@Components/Product/Slider/Video'

import styles from './HorizontalThumbnailSlider.module.scss'

const HorizontalThumbnailSlider: FC = () => {
  const {
    activeSlide,
    isFullscreen,
    setActiveSlide,
    slides,
  } = useContext(SliderContext)
  const product = useContext(ProductContext)
  const ampli = useAmplitude()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const scrollToActive = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const wrapper = wrapperRef.current
    const track = trackRef.current
    if (!wrapper || !track) {
      return
    }

    const activeElement = track.children[activeSlide.key] as HTMLElement | undefined
    if (!activeElement) {
      return
    }

    const wrapperRect = wrapper.getBoundingClientRect()
    const activeRect = activeElement.getBoundingClientRect()

    const currentScrollLeft = wrapper.scrollLeft
    const activeOffsetLeft = activeRect.left - wrapperRect.left
    const targetScrollLeft = currentScrollLeft
      + activeOffsetLeft
      - (wrapperRect.width / 2)
      + (activeRect.width / 2)

    const maxScrollLeft = wrapper.scrollWidth - wrapperRect.width
    const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, Math.max(0, maxScrollLeft)))

    if (Math.abs(clampedScrollLeft - currentScrollLeft) < 1) {
      return
    }

    wrapper.scrollTo({ left: clampedScrollLeft, behavior })
  }, [activeSlide.key])

  useEffect(() => {
    scrollToActive('smooth')
  }, [activeSlide.key, scrollToActive])

  if (slides.length < 3) {
    return null
  }

  return (
    <div className={clsx(styles.wrapper, isFullscreen && styles.fullscreen)} ref={wrapperRef}>
      <div className={styles.track} ref={trackRef}>
        {map(slides, (item, key) => (
          <button
            key={key}
            aria-label={`Select image ${key + 1}`}
            className={`${styles.thumbnail} ${activeSlide.key === key ? styles.active : ''}`}
            onClick={() => {
              ampli.galleryInteracted({
                asset_position: key + 1,
                asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide.tags?.includes('look')),
                has_video: slides?.some(slide => slide.tags?.includes('video')),
                type: 'click',
              })
              setActiveSlide({
                key,
                recalculate: true,
              })
            }}
            onFocus={() => {
              ampli.galleryInteracted({
                asset_position: key + 1,
                asset_type: slides[key]?.tags?.includes('video') ? 'video' : 'image',
                has_look: slides?.some(slide => slide.tags?.includes('look')),
                has_video: slides?.some(slide => slide.tags?.includes('video')),
                type: 'focus',
              })
              setActiveSlide({
                key,
                recalculate: true,
              })
            }}
            type="button"
          >
            {
              includes(item.tags, 'video')
                ? <div className={styles.video}><VideoVertical asset={first(slides)} size={80} /></div>
                : <ImageVertical asset={item} height={80} index={key} title={product?.name || ''} width={80} />
            }
          </button>
        ))}
      </div>
    </div>
  )
}

export default HorizontalThumbnailSlider
